import React, { useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';

// COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../../common/SortableHeader';
import PaginatorFooter from '../../common/PaginatorFooter';
import ColumnSelector from '../../common/ColumnSelector';
import TableContainer from '../../common/TableContainer';   
import Icons from '../../common/Icons';

// MODALES Y COMPONENTES UI EXISTENTES
import FiltrosDinamicos from '../../../../ui/FiltrosDinamicos.jsx';
import ModalProspecto from '../../../../ui/ModalProspecto'; 
import FormularioCotizacionArt from '../../../../Formularios/FormularioCotizacionArt';
import FormularioPedidoCotizacionART from '../../../../Formularios/FormularioPedidoCotizacionART'; 
import DetalleCotizacionArt from '../DetalleCotizaciones/DetalleCotizacionArt.jsx';
import AuthModal from '../../../../ui/AuthModal'; 
import BuscadorInteligente from '../../../../ui/BuscadorInteligente';

// CONTROLLER DE NEGOCIO Y PERMISOS CENTRALIZADOS
import { useCotizacionesArtTable } from './useCotizacionesArtTable';
import { usePermissions } from '../../../../hooks/usePermissions';
import { estadoCotizacionArt, tipoGestionArt, getPersonaChipClass } from '../../../../../utils/styles';

const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
        const cleanDate = dateStr.split('T')[0];
        const parts = cleanDate.split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
    }
    return dateStr;
};

const TableRow = memo(({ item, isSelected, columnVisibility, onSelect, onAction, isFullUser }) => {
    return (
        <tr 
            onClick={() => onSelect(item.id)} 
            className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 
                ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'}`}
        >
            {columnVisibility.fechaCarga && (
                <td className="p-3 px-3 border-b border-r border-slate-100 text-center text-xs text-slate-500 tabular-nums">
                    {formatDateDisplay(item.fechaCarga)}
                </td>
            )}
            {columnVisibility.displayNombre && (
                <td className="p-3 px-4 border-b border-r border-slate-100 text-sm font-bold text-slate-900 truncate uppercase text-left" title={item.displayNombre}>
                    {item.displayNombre || 'SIN NOMBRE'}
                </td>
            )}
            {columnVisibility.displayDoc && (
                <td className="p-3 px-3 border-b border-r border-slate-100 text-center text-xs font-mono text-slate-500">
                    {item.displayDoc}
                </td>
            )}
            {columnVisibility.numeroCotizacion && (
                <td className="p-3 px-3 border-b border-r border-slate-100 text-center font-bold text-blue-700 text-xs font-mono">
                    {item.numeroCotizacion || 'S/N'}
                </td>
            )}
            {columnVisibility.tipoEntidad && (
                <td className="p-3 px-2 border-b border-r border-slate-100 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${item.tipoEntidad === 'CLIENTE' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-purple-50 text-purple-700 border-purple-100'}`}>
                        {item.tipoEntidad}
                    </span>
                </td>
            )}
            {columnVisibility.tipoGestion && (
                <td className="p-3 px-2 border-b border-r border-slate-100 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${tipoGestionArt(item.tipoGestion)}`}>
                        {item.tipoGestion || '-'}
                    </span>
                </td>
            )}
            {columnVisibility.estado && (
                <td className="p-3 px-2 border-b border-r border-slate-100 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${estadoCotizacionArt(item.estado)}`}>
                        {item.estado}
                    </span>
                </td>
            )}
            {columnVisibility.vendedor && (
                <td className="p-3 px-2 border-b border-r border-slate-100 text-center">
                     {item.vendedor ? <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit mx-auto ${getPersonaChipClass(item.vendedor)}`}><span className="text-xs font-bold">{item.vendedor}</span></div> : '-'}
                </td>
            )}
            {columnVisibility.responsableAsignado && (
                <td className="p-3 px-2 border-b border-r border-slate-100 text-center">
                     {item.responsableAsignado ? <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit mx-auto ${getPersonaChipClass(item.responsableAsignado)}`}><span className="text-xs font-bold">{item.responsableAsignado}</span></div> : '-'}
                </td>
            )}
            {columnVisibility.canal && <td className="p-3 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-500 uppercase truncate" title={item.canal}>{item.canal || '-'}</td>}
            {columnVisibility.canalNombre && <td className="p-3 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-700 uppercase truncate" title={item.canalNombre}>{item.canalNombre || '-'}</td>}
            {columnVisibility.subcanalNombre && <td className="p-3 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-500 uppercase truncate" title={item.subcanalNombre}>{item.subcanalNombre || '-'}</td>}

            {/* ACCIONES */}
            <td className="p-3 px-2 border-b border-slate-100 text-center sticky right-0 z-20 bg-white group-hover:bg-slate-50 border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)]">
                <div className="flex justify-center items-center gap-1.5">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'VIEW'); }} className="p-1 text-slate-400 hover:text-blue-600 transition-all active:scale-90 cursor-pointer" title="Ver Detalle"><Icons.Eye /></button>
                    {isFullUser && (
                        <>
                            {item.tipoEntidad === 'PROSPECTO' && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'PROSPECT_EDIT'); }} className="p-1 text-slate-400 hover:text-indigo-600 transition-all cursor-pointer" title="Editar Prospecto"><Icons.User /></button>
                            )}
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'EDIT'); }} className="p-1 text-slate-400 hover:text-orange-600 transition-all active:scale-90 cursor-pointer" title="Editar"><Icons.Edit /></button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'DELETE'); }} className="p-1 text-slate-400 hover:text-red-600 transition-all active:scale-90 cursor-pointer" title="Eliminar"><Icons.Trash /></button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

const CotizacionesArt = () => {
    const navigate = useNavigate();

    const {
        view,
        setView,
        step,
        setStep,
        loading,
        searchResults,
        setSearchResults,
        searchPerformed,
        setSearchPerformed,
        loadingSearch,
        setLoadingSearch,
        selectedClient,
        setSelectedClient,
        isProspect,
        setIsProspect,
        editingCotizacion,
        detailCotizacion,
        setDetailCotizacion,
        showProspectModal,
        setShowProspectModal,
        editingProspectData,
        setEditingProspectData,
        authModalConfig,
        setAuthModalConfig,
        tableState,
        itemsTable,
        handleCreateNew,
        handleAction,
        confirmDelete,
        handleSuccess
    } = useCotizacionesArtTable();

    const { can, isFullUser, loadingAuth } = usePermissions();

    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange
    } = tableState;

    const columnLabels = {
        fechaCarga: "Fecha Carga", displayNombre: "Cliente / Prospecto", displayDoc: "CUIT / DNI",
        numeroCotizacion: "N° Cotización", tipoEntidad: "Tipo", tipoGestion: "Gestión",
        estado: "Estado", canal: "Canal / Org", canalNombre: "Nombre Canal",
        subcanalNombre: "Subcanal / Asociado", vendedor: "Vendedor", responsableAsignado: "Responsable Asignado"
    };

    const handleActionCallback = useCallback((item, type) => {
        handleAction(item, type, navigate);
    }, [handleAction, navigate]);

    // EXPORTACIÓN A EXCEL DINÁMICA (SOLO COMPAÑÍAS CON DATOS Y NOMBRES LIMPIOS)
    const handleExportExcel = useCallback(async () => {
        if (!filteredData || filteredData.length === 0) {
            alert('No hay registros filtrados para exportar.');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Aumentos y Cotizaciones ART');

        const cleanCompName = (str) => {
            if (!str) return '';
            return str.toString().replace(/\s+ART$/i, '').trim().toUpperCase();
        };

        // Extraer únicamente las compañías con cotizaciones cargadas
        const masterCompaniasSet = new Set();
        filteredData.forEach(item => {
            if (Array.isArray(item.cotizaciones)) {
                item.cotizaciones.forEach(c => {
                    const cleaned = cleanCompName(c.compania);
                    if (cleaned) masterCompaniasSet.add(cleaned);
                });
            }
        });

        const masterCompanias = Array.from(masterCompaniasSet).sort();

        const columnsBase = [
            { header: 'N° Cotización', key: 'numeroCotizacion', width: 16, type: 'text', styleType: 'black' },
            { header: 'Mes de Aumento', key: 'mesAumento', width: 14, type: 'text', styleType: 'black' },
            { header: 'Periodo Arca', key: 'periodoArca', width: 14, type: 'text', styleType: 'black' },
            { header: 'N° Contrato', key: 'nContratoOrigen', width: 14, type: 'text', styleType: 'black' },
            { header: 'CUIT / DNI', key: 'displayDoc', width: 16, type: 'text', styleType: 'black' },
            { header: 'Razón Social', key: 'displayNombre', width: 35, type: 'text', styleType: 'black' },
            { header: 'Compañía', key: 'compania', width: 16, type: 'text', styleType: 'black' },
            { header: 'Cápitas', key: 'capitas', width: 10, type: 'number', styleType: 'black' },
            { header: 'Masa Salarial', key: 'masaSalarial', width: 22, type: 'currency', styleType: 'black' },
            { header: 'Alicuota Actual', key: 'alicuotaActual', width: 14, type: 'percent', styleType: 'black' },
            { header: 'Alicuota Aumentada', key: 'alicuotaAumentada', width: 16, type: 'percent', styleType: 'yellow' },
            { header: 'Porcentaje de Aumento', key: 'porcentajeAumento', width: 18, type: 'percent', styleType: 'red' },
            { header: 'Vendedor', key: 'vendedor', width: 18, type: 'text', styleType: 'green' },
            { header: 'Canal', key: 'canal', width: 14, type: 'text', styleType: 'green' },
            { header: 'Nombre Canal', key: 'canalNombre', width: 20, type: 'text', styleType: 'green' },
            { header: 'Subcanal', key: 'subcanalNombre', width: 20, type: 'text', styleType: 'green' },
            { header: 'Estado', key: 'estado', width: 16, type: 'text', styleType: 'green' },
            { header: 'Observaciones', key: 'observaciones', width: 30, type: 'text', styleType: 'green' },
        ];

        masterCompanias.forEach(comp => {
            columnsBase.push({
                header: comp,
                key: `comp_${comp}`,
                width: 15,
                type: 'company',
                styleType: 'gray'
            });
        });

        worksheet.columns = columnsBase.map(col => ({ header: col.header, key: col.key, width: col.width }));

        const getValorCompania = (cotizacionesArray, compNombreLimpio) => {
            if (!Array.isArray(cotizacionesArray)) return '';
            const cotFound = cotizacionesArray.find(c => cleanCompName(c.compania) === compNombreLimpio);
            if (!cotFound) return '';

            const estado = (cotFound.estadoCotizacion || '').trim();
            if (cotFound.alicuotaCotizada && (estado.toLowerCase() === 'cotizada' || estado === '')) {
                const num = parseFloat(String(cotFound.alicuotaCotizada).replace(',', '.'));
                return isNaN(num) ? cotFound.alicuotaCotizada : (num / 100);
            }

            return estado || 'Solicitada';
        };

        filteredData.forEach(item => {
            const rowObj = {
                numeroCotizacion: item.numeroCotizacion || 'S/N',
                mesAumento: item.mesAumento || '-',
                periodoArca: item.periodoArca || '-',
                nContratoOrigen: item.nContratoOrigen || '-',
                displayDoc: item.displayDoc || '-',
                displayNombre: item.displayNombre || '-',
                compania: item.artActual || item.compania || '-',
                capitas: Number(item.capitas || 0),
                masaSalarial: Number(item.masaSalarial || 0),
                alicuotaActual: Number(item.alicuotaActual || 0) / 100,
                alicuotaAumentada: Number(item.alicuotaAumentada || 0) / 100,
                porcentajeAumento: Number(item.porcentajeAumento || 0) / 100,
                vendedor: item.vendedor || '-',
                canal: item.canal || '-',
                canalNombre: item.canalNombre || '-',
                subcanalNombre: item.subcanalNombre || '-',
                estado: item.estado || '-',
                observaciones: item.observaciones || '-'
            };

            masterCompanias.forEach(comp => {
                rowObj[`comp_${comp}`] = getValorCompania(item.cotizaciones, comp);
            });

            const addedRow = worksheet.addRow(rowObj);

            columnsBase.forEach((col, colIdx) => {
                const cell = addedRow.getCell(colIdx + 1);
                if (col.type === 'currency') {
                    cell.numFmt = '"$"#,##0.00';
                } else if (col.type === 'percent') {
                    cell.numFmt = '0.00%';
                } else if (col.type === 'company' && typeof cell.value === 'number') {
                    cell.numFmt = '0.00%';
                }

                cell.border = {
                    top: { style: 'thin', color: { argb: 'D1D5DB' } },
                    left: { style: 'thin', color: { argb: 'D1D5DB' } },
                    bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
                    right: { style: 'thin', color: { argb: 'D1D5DB' } }
                };
            });
        });

        const headerRow = worksheet.getRow(1);
        headerRow.height = 30;

        columnsBase.forEach((col, colIdx) => {
            const cell = headerRow.getCell(colIdx + 1);
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

            if (col.styleType === 'black') {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '000000' } };
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
            } else if (col.styleType === 'yellow') {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '000000' } };
            } else if (col.styleType === 'red') {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CC0000' } };
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
            } else if (col.styleType === 'green') {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9EAD3' } };
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '274E13' } };
            } else if (col.styleType === 'gray') {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9D9D9' } };
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '000000' } };
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        const now = new Date();
        const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        
        anchor.href = url;
        anchor.download = `Reporte_Cotizaciones_ART_${dateStr}.xlsx`;
        anchor.click();
        window.URL.revokeObjectURL(url);
    }, [filteredData]);

    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(item => item[key]).filter(Boolean))].sort().map(v => ({label: v, value: v}));
        return [
            { name: 'search', label: 'Buscador General', type: 'text', placeholder: 'Cliente, Cotización, CUIT o Póliza...', className: 'w-80' },
            { 
                name: 'cotizacionPendiente', 
                label: 'Cotiz. Pendientes', 
                type: 'select', 
                isMulti: true, 
                options: [{ label: 'SÍ (Cías Solicitadas)', value: 'SI' }] 
            },
            { name: 'fechaCarga', label: 'Fecha de Carga', type: 'date-range' },
            { name: 'tipoIngreso', label: 'Tipo', type: 'select', isMulti: true, options: [{label: 'CLIENTE', value: 'CLIENTE'}, {label: 'PROSPECTO', value: 'PROSPECTO'}] },
            { name: 'tipoGestion', label: 'Gestión', type: 'select', isMulti: true, options: getUnique('tipoGestion') },
            { name: 'estado', label: 'Estado', type: 'select', isMulti: true, options: getUnique('estado') },
            { name: 'vendedor', label: 'Vendedor', type: 'select', isMulti: true, options: getUnique('vendedor') },
            { name: 'responsableAsignado', label: 'Responsable Asignado', type: 'select', isMulti: true, options: getUnique('responsableAsignado') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique('canal') },
            { name: 'canalNombre', label: 'Nombre del Canal', type: 'select', isMulti: true, options: getUnique('canalNombre') },
            { name: 'subcanalNombre', label: 'Subcanal', type: 'select', isMulti: true, options: getUnique('subcanalNombre') },
        ];
    }, [itemsTable]);

    const sharedUI = (
        <React.Fragment>
            <AuthModal 
                isOpen={authModalConfig.open} 
                onClose={() => setAuthModalConfig({ open: false, type: '', item: null })} 
                onConfirm={confirmDelete} 
                title="Eliminar Cotización" 
                message={authModalConfig.item?.tipoGestion === 'Aumento de Tarifa' 
                    ? "Al eliminar esta cotización, se desvinculará del endoso de aumento y este retornará al estado 'Pendiente' en forma atómica. ¿Confirmas la acción?"
                    : "¿Estás seguro que deseas borrar esta cotización? Esta acción no se puede deshacer."} 
            />
            <ModalProspecto 
                show={showProspectModal} 
                onClose={() => { setShowProspectModal(false); setEditingProspectData(null); }} 
                initialData={editingProspectData} 
                onSaveSuccess={() => setShowProspectModal(false)} 
                isProspect={true}
            />
        </React.Fragment>
    );

    if (loading || loadingAuth) return (
        <div className="flex items-center justify-center h-screen bg-white text-slate-400 font-bold uppercase tracking-widest text-[10px] animate-pulse">
            Sincronizando tablero de cotizaciones...
        </div>
    );

    if (view !== 'list') {
        if (view === 'detail') return <div className="h-screen w-full bg-white overflow-y-auto animate-fade-in text-left"><DetalleCotizacionArt cotizacion={detailCotizacion} onBack={() => { setDetailCotizacion(null); setView('list'); }} /></div>;
        
        return (
            <div className="animate-fade-in p-10 bg-slate-50 min-h-screen text-left">
                <div className="max-w-5xl mx-auto">
                    <button onClick={() => { setView('list'); setStep(1); }} className="text-slate-400 hover:text-slate-800 flex items-center gap-2 text-sm font-bold mb-6 transition-colors text-left text-[10px] uppercase tracking-widest cursor-pointer">← Volver al Tablero</button>
                    
                    {/* PASO 1 COMPARTIDO */}
                    {(view === 'create' || view === 'request') && step === 1 && (
                        <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200 text-left">
                            <h2 className="text-lg font-bold text-slate-800 mb-6 text-left">Buscar Cliente / Prospecto</h2>
                            <BuscadorInteligente scope="global_entidades" onResult={(r) => { setSearchResults(r); setSearchPerformed(true); }} onLoading={setLoadingSearch} placeholder="DNI, CUIT o Nombre..." autoFocus={true} />
                            
                            {!loadingSearch && searchPerformed && (
                                <div className="space-y-4 mt-8">
                                    {searchResults.length > 0 ? (
                                        searchResults.map(client => (
                                            <div key={client.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex justify-between items-center hover:border-blue-300 transition-all cursor-pointer group text-left" onClick={() => { setSelectedClient(client); setIsProspect(client.type === 'prospecto'); setStep(2); }}>
                                                <div className="flex items-center gap-4 text-left"><div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-left">{client.nombre?.charAt(0)}{client.apellido?.charAt(0)}</div><div className="text-left"><h4 className="font-bold text-slate-800 group-hover:text-blue-700 text-left">{client.nombre} {client.apellido}</h4><p className="text-xs text-slate-500 font-mono text-left">{client.dni || client.cuit}</p></div></div>
                                                <span className="text-xs font-bold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity text-left">Seleccionar</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="py-16 flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50 animate-fade-in text-left">
                                            <div className="mb-4"><Icons.User className="w-16 h-16 text-slate-300" /></div>
                                            <p className="text-slate-500 font-bold text-lg mb-6">No se encontró al cliente en nuestra base de datos.</p>
                                            <button onClick={(e) => { e.stopPropagation(); setEditingProspectData(null); setShowProspectModal(true); }} className="bg-orange-600 text-white px-8 py-3 rounded-xl font-black shadow-lg hover:bg-orange-700 transition-all uppercase text-xs tracking-widest flex items-center gap-2 transform active:scale-95 cursor-pointer"><Icons.Plus /> Alta de Prospecto</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* PASO 2 */}
                    {step === 2 && (
                        <>
                            {(view === 'create' || view === 'edit') && (
                                <FormularioCotizacionArt 
                                    entityData={selectedClient} 
                                    isProspect={isProspect} 
                                    initialData={editingCotizacion} 
                                    onSuccess={handleSuccess} 
                                    onCancel={() => { setView('list'); setStep(1); }} 
                                />
                            )}

                            {view === 'request' && (
                                <FormularioPedidoCotizacionART 
                                    entityData={selectedClient} 
                                    isProspect={isProspect} 
                                    onSuccess={handleSuccess} 
                                    onCancel={() => { setView('list'); setStep(1); }} 
                                />
                            )}
                        </>
                    )}
                </div>
                {sharedUI}
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen w-full bg-white text-slate-900 overflow-hidden antialiased font-sans text-left">
            <div className="h-1 bg-blue-600 w-full shrink-0"></div>
            <header className="h-20 shrink-0 bg-white border-b border-slate-200 px-8 flex items-center justify-between z-[110] relative shadow-sm text-left">
                <div className="flex items-center gap-6 text-left">
                    <button onClick={() => navigate('/CargaCotizacionArtPage')} className="p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all border border-transparent hover:border-blue-100 cursor-pointer"><Icons.ArrowLeft /></button>
                    <div className="flex items-center gap-4 text-left">
                        <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl border border-blue-100 shadow-sm text-left"><Icons.Policy /></div>
                        <div className="text-left">
                            <h2 className="text-xl font-black text-slate-800 tracking-tight leading-tight uppercase italic text-left">Tablero de Cotizaciones</h2>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mt-1 text-left">Gestión integral de producción y negociaciones ART</p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4 text-left">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />

                    {/* BOTÓN DE EXPORTACIÓN A EXCEL */}
                    <button 
                        onClick={handleExportExcel} 
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-md active:scale-95 flex items-center gap-2 transition-all cursor-pointer"
                        title="Exportar registros filtrados a Excel"
                    >
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm10.5-7L14 15.5 11.5 13 10 14.5l2.5 2.5L10 19.5l1.5 1.5 2.5-2.5 2.5 2.5 1.5-1.5-2.5-2.5 2.5-2.5-1.5-1.5z"/>
                        </svg>
                        Excel
                    </button>

                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-2xl p-1 pr-5 shadow-inner text-left">
                        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-xl text-lg font-black shadow-md text-left">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500 text-left">Registros</span>
                    </div>

                    {can('CREAR_COTIZACION') ? (
                        <button onClick={handleCreateNew} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 text-sm font-bold shadow-lg active:scale-95 flex items-center gap-2 whitespace-nowrap text-left cursor-pointer">
                            <Icons.Plus /> Nueva Cotización
                        </button>
                    ) : (
                        <button 
                            onClick={() => { 
                                setView('request'); 
                                setStep(1); 
                                setSelectedClient(null); 
                                setSearchResults([]); 
                                setSearchPerformed(false); 
                            }} 
                            className="bg-orange-600 text-white px-5 py-2.5 rounded-xl hover:bg-orange-700 text-sm font-bold shadow-lg animate-pulse active:scale-95 text-left cursor-pointer"
                        >
                            Pedir Cotización
                        </button>
                    )}
                </div>
            </header>

            {/* FILTROS DINÁMICOS PERFECTAMENTE ALINEADOS */}
            <FiltrosDinamicos 
                key="cotizaciones-art-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            <main className="flex-grow overflow-hidden flex flex-col bg-white text-left">
                <TableContainer minWidth="2400px">
                    <thead>
                        <tr className="text-white text-left">
                            <SortableHeader label="Fecha Carga" sortKey="fechaCarga" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Cliente / Prospecto" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-72 text-left" />
                            <SortableHeader label="CUIT / DNI" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="N° Cotización" sortKey="numeroCotizacion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center font-mono" />
                            <SortableHeader label="Tipo" sortKey="tipoEntidad" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="Gestión" sortKey="tipoGestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Vendedor" sortKey="vendedor" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Responsable Asignado" sortKey="responsableAsignado" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44 text-center" />
                            <SortableHeader label="Canal / Org" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-left" />
                            <SortableHeader label="Nombre Canal" sortKey="canalNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <SortableHeader label="Subcanal / Asociado" sortKey="subcanalNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <th className="w-32 p-4 px-4 text-center sticky right-0 top-0 z-50 bg-slate-900 border-b border-r border-slate-700 uppercase tracking-wider text-[10px] font-bold shadow-[-4px_0_10px_rgba(0,0,0,0.1)] text-white text-left">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-left">
                        {paginatedData.map((item) => (
                            <TableRow key={item.id} item={item} isSelected={selectedRowId === item.id} columnVisibility={columnVisibility} onSelect={setSelectedRowId} onAction={handleActionCallback} isFullUser={isFullUser} />
                        ))}
                    </tbody>
                </TableContainer>
            </main>

            <PaginatorFooter 
                currentPage={currentPage}
                totalPages={totalPages}
                currentLength={paginatedData.length}
                totalLength={filteredData.length}
                onPageChange={setCurrentPage}
                label="registros"
                accentClass="text-blue-900 bg-blue-50 border-blue-100"
            />
            {sharedUI}

            <style>{`
                .custom-scrollbar { text-align: left; }
                .custom-scrollbar::-webkit-scrollbar { height: 10px; width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f8fafc; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; border: 3px solid #f8fafc; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                table { border-collapse: separate; }
                .tabular-nums { font-variant-numeric: tabular-nums; }
            `}</style>
        </div>
    );
};

export default CotizacionesArt;